/**
 * DL-01 decision 2 — the owner's per-project switch
 * `projects.customer_image_download` (default OFF), read off the project
 * (projects/v1 `GET /projects/{id}`) for a CUSTOMER row only
 * (`eligibility.ts:needsCustomerFlag`): the action is hidden until the owner
 * turned it on. Several rows of one project share the one query.
 *
 * The field is `ProjectDetail.customerImageDownload` (projects/v1 2.27.0,
 * story §A-4). A read the server refuses (a member without the detail read,
 * a 404) counts as OFF — the server is the boundary either way (§A-3).
 */
import { useQuery } from "@tanstack/react-query";
import { getProjectsBaseUrl, useAuthStore } from "@aeriosiq/auth-client";
import type { ProjectsComponents } from "@aeriosiq/types";

export const customerFlagKeys = {
  all: ["image-download", "customer-flag"] as const,
  project: (projectId: string) => [...customerFlagKeys.all, projectId] as const,
};

type ProjectDetail = ProjectsComponents["schemas"]["ProjectDetail"];

/** The flag off a served project (`ProjectDetail.customerImageDownload`,
 *  projects/v1 2.27.0); an older body without it reads as off. */
export function customerImageDownloadOf(body: unknown): boolean {
  if (!body || typeof body !== "object") return false;
  return (body as Partial<ProjectDetail>).customerImageDownload === true;
}

export async function fetchCustomerImageDownloadFlag(
  projectId: string,
): Promise<boolean> {
  const { session, tenant } = useAuthStore.getState();
  const headers = new Headers();
  if (session?.accessToken)
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  if (tenant?.tenantSlug) headers.set("X-Tenant-ID", tenant.tenantSlug);
  const res = await fetch(
    `${getProjectsBaseUrl()}/projects/${encodeURIComponent(projectId)}`,
    { method: "GET", headers },
  );
  if (!res.ok) return false;
  return customerImageDownloadOf(await res.json());
}

/**
 * `true` / `false` once read, `null` while unknown or when not asked
 * (`enabled: false`, no project). Tenant-readiness gated like every
 * tenant-scoped query (`reference_tenant_scoped_query_enabled_gate`).
 */
export function useCustomerImageDownloadFlag(
  projectId: string | null | undefined,
  enabled: boolean,
): boolean | null {
  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);
  const tenantReady =
    !!session && !!tenant && session.tenant.tenantSlug === tenant.tenantSlug;
  const id =
    typeof projectId === "string" && projectId !== "" ? projectId : null;
  const q = useQuery<boolean>({
    queryKey: customerFlagKeys.project(id ?? ""),
    queryFn: () => fetchCustomerImageDownloadFlag(id ?? ""),
    enabled: enabled && tenantReady && id !== null,
    staleTime: 60_000,
    retry: false,
  });
  if (!enabled || id === null) return null;
  return q.data ?? null;
}
